import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import AgentPausesAPI from 'dashboard/api/agentPauses';

const defaultDashboard = () => ({
  pending_requests: [],
  pauses: [],
  meta: {
    pending_count: 0,
    active_count: 0,
    completed_today_count: 0,
  },
});

const normalizeDashboard = data => ({
  ...defaultDashboard(),
  ...(data || {}),
  meta: {
    ...defaultDashboard().meta,
    ...(data?.meta || {}),
  },
});

export function useSupervisorPauses({ pollIntervalMs = 15000 } = {}) {
  const { t } = useI18n();
  const currentRole = useMapGetter('getCurrentRole');

  const dashboard = ref(defaultDashboard());
  const isFetching = ref(false);
  const loadingActionId = ref(null);
  let pollingIntervalId = null;

  const canManagePauses = computed(() =>
    ['administrator', 'supervisor'].includes(currentRole.value)
  );
  const pendingRequests = computed(() => dashboard.value.pending_requests);
  const pauses = computed(() => dashboard.value.pauses);
  const pendingCount = computed(() => dashboard.value.meta.pending_count);
  const activeCount = computed(() => dashboard.value.meta.active_count);
  const completedTodayCount = computed(
    () => dashboard.value.meta.completed_today_count
  );

  const updateDashboard = data => {
    dashboard.value = normalizeDashboard(data);
  };

  const fetchDashboard = async ({ silent = false } = {}) => {
    if (!canManagePauses.value) {
      updateDashboard(defaultDashboard());
      return;
    }

    if (!silent) {
      isFetching.value = true;
    }

    try {
      const response = await AgentPausesAPI.list();
      updateDashboard(response.data);
    } catch (error) {
      if (!silent) {
        useAlert(t('AGENT_MGMT.PAUSES.API.FETCH_ERROR'));
      }
    } finally {
      if (!silent) {
        isFetching.value = false;
      }
    }
  };

  const approveRequest = async request => {
    loadingActionId.value = request.id;

    try {
      await AgentPausesAPI.approve(request.id);
      useAlert(t('AGENT_MGMT.PAUSES.API.APPROVE_SUCCESS'));
      await fetchDashboard({ silent: true });
    } catch (error) {
      useAlert(
        error?.response?.data?.error || t('AGENT_MGMT.PAUSES.API.APPROVE_ERROR')
      );
    } finally {
      loadingActionId.value = null;
    }
  };

  const rejectRequest = async request => {
    loadingActionId.value = request.id;

    try {
      await AgentPausesAPI.reject(request.id);
      useAlert(t('AGENT_MGMT.PAUSES.API.REJECT_SUCCESS'));
      await fetchDashboard({ silent: true });
    } catch (error) {
      useAlert(
        error?.response?.data?.error || t('AGENT_MGMT.PAUSES.API.REJECT_ERROR')
      );
    } finally {
      loadingActionId.value = null;
    }
  };

  const startPolling = () => {
    if (pollIntervalMs <= 0 || pollingIntervalId || !canManagePauses.value) {
      return;
    }

    pollingIntervalId = window.setInterval(() => {
      fetchDashboard({ silent: true });
    }, pollIntervalMs);
  };

  const stopPolling = () => {
    if (!pollingIntervalId) return;

    window.clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  };

  useEmitter('agent_pause_updated', () => fetchDashboard({ silent: true }));

  onMounted(() => {
    fetchDashboard();
    startPolling();
  });

  onBeforeUnmount(() => {
    stopPolling();
  });

  return {
    dashboard,
    pendingRequests,
    pauses,
    pendingCount,
    activeCount,
    completedTodayCount,
    isFetching,
    loadingActionId,
    canManagePauses,
    fetchDashboard,
    approveRequest,
    rejectRequest,
  };
}
